import {
  IconButton,
  Label,
  EditIcon,
  DialogTrigger,
} from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import { useMemo } from "react"

import styles from "./AccountDetails.module.scss"
import { ChangePasswordDialog } from "./ChangePasswordDialog"
import { DeleteAccount } from "./DeleteAccount"
import { Firstname } from "./Firstname"
import { GlucoseUnits } from "./GlucoseUnits"
import { Lastname } from "./Lastname"
import { LogOut } from "./LogOut"
import { Nickname } from "./Nickname"
import { PhoneNumber } from "./PhoneNumber"
import { useOnSubmitPhone } from "./useOnSubmitPhone"
import { ViewableDetails } from "./ViewableDetails"
import {
  PageTemplate,
  PhoneNumberModal,
} from "src/components"
import { usePolyglot } from "src/contexts"
import {
  useAccount,
  useIsUS,
} from "src/hooks"

const polyglotPrefix = "pages.account.account_details."

export function AccountDetails() {
  const polyglot = usePolyglot()

  const account = useAccount()
  const {
    birthdate,
    country,
    id: accountId,
  } = account.data || {}

  const formattedBirthdate = useMemo(
    () => {
      if (!birthdate) {
        return ""
      }

      return new Intl.DateTimeFormat(
        polyglot.locale(),
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        },
      ).format(dayjs(birthdate).toDate())
    },
    [
      birthdate,
      polyglot,
    ],
  )

  const formattedCountry = useMemo(
    () => {
      if (!country) {
        return ""
      }

      return polyglot.t(`country.${country.toUpperCase()}`) || country
    },
    [
      country,
      polyglot,
    ],
  )

  const onSubmitPhone = useOnSubmitPhone()
  const isUSAccount = useIsUS(country)

  return (
    <PageTemplate
      user="account"
      title={polyglot.t("components.page_header.pages.details")}
      backRoutePath="/account"
    >
      <div className={styles.container}>
        <div className={styles.section}>
          <Label>
            {polyglot.t(`${polyglotPrefix}personal_info.title`)}
          </Label>
          <Firstname />
          <Lastname />
          <ViewableDetails
            id={"user-date-of-birth"}
            title={polyglot.t(`${polyglotPrefix}dob.label`)}
            content={formattedBirthdate}
          />
          <ViewableDetails
            id={"user-country-residence"}
            title={polyglot.t(`${polyglotPrefix}country.residence`)}
            content={formattedCountry}
          />
          <Nickname accountId={accountId ?? -1} />
        </div>
        <div className={styles.section}>
          <Label>
            {polyglot.t(`${polyglotPrefix}account_info.title`)}
          </Label>
          <ViewableDetails
            id={"user-email"}
            title={polyglot.t(`${polyglotPrefix}email.label`)}
            content={account.data?.email || ""}
          />
          <PhoneNumberModal onSubmit={onSubmitPhone}>
            <PhoneNumber />
          </PhoneNumberModal>
        </div>
        <ChangePasswordDialog>
          <div className={styles.changePasswordRow}>
            <Label>
              {polyglot.t(`${polyglotPrefix}change_password.label`)}
            </Label>
            <DialogTrigger asChild>
              <IconButton id="change-password-btn">
                <EditIcon />
              </IconButton>
            </DialogTrigger>
          </div>
        </ChangePasswordDialog>
        {!isUSAccount && <GlucoseUnits />}
        <div className={styles.buttonsContainer}>
          <DeleteAccount />
          <LogOut />
        </div>
      </div>
    </PageTemplate>
  )
}
